import {emptyList, ItemList} from '../../../common/models/ItemList'
import {Observable, of} from 'rxjs'
import {Container, IInit} from '../../../common/container/Container'
import {HTTP_CLIENT_KEY, IHTTPClient} from '../../../common/api/HTTPClient'
import {IStatusService} from '../../../common/status/StatusService'
import {STATUS_SERVICE_KEY} from '../../../container/app'
import {Query} from '../../../common/api/Query'
import {Individual, IndividualQuery, Informativity, InformativityQuery} from '../models/Individual'
import {IndividualContainerConfig} from '../container'
import {IndividualDTO, toModel} from '../models/Individual'
import {prepareURL} from '../../../common/api/http-helpers'
import {catchError, map} from 'rxjs/operators'
import {ProgressState} from '../../../common/models/ProgressState'

export interface IIndividualApi extends IInit {
    getByID(id: string): Observable<Individual | undefined>

    getFilteredList(q: Query<IndividualQuery>): Observable<ItemList<Individual>>

    add(e: IndividualDTO): Observable<Individual | undefined>

    update(e: IndividualDTO): Observable<Individual | undefined>

    update2(e: IndividualDTO): Observable<Individual | undefined>


    delete(id: string): Observable<boolean>

    getInformativityList(q: Query<InformativityQuery>): Observable<ItemList<Informativity>>

    updateInformativity(e: Informativity): Observable<Informativity | undefined>

    runParentsSnipetsProcess(projectID: string): Observable<boolean>

    updateBamFiles(e: IndividualDTO): Observable<Individual | undefined>

    getProgress(projectID: string): Observable<ProgressState>
}

export class IndividualApi implements IIndividualApi {
    private _container!: Container
    private _httpClient!: IHTTPClient
    private _url!: string
    private _statusService!: IStatusService

    init(c: Container) {
        this._container = c
        this._httpClient = this._container.get<IHTTPClient>(HTTP_CLIENT_KEY)
        this._statusService = this._container.get<IStatusService>(STATUS_SERVICE_KEY)
        this._url = (this._container.config as IndividualContainerConfig).moduleFullUrl
    }

    add(e: IndividualDTO): Observable<Individual | undefined> {
        return this._httpClient.post<Individual>({url: this._url, body: e}).pipe(
            map<IndividualDTO, Individual>((d) => toModel(d)),
            catchError((err) => {
                this._statusService.sendStatus({variant: 'error', error: err})
                return of(undefined)
            })
        )
    }

    delete(id: string): Observable<boolean> {
        return this._httpClient.delete({url: this._url + '/' + id}).pipe(
            catchError((err) => {
                this._statusService.sendStatus({variant: 'error', error: err})
                return of(false)
            })
        )
    }

    getByID(id: string): Observable<Individual | undefined> {
        return this._httpClient.get<Individual>({url: `${this._url}/${id}`}).pipe(
            map<IndividualDTO, Individual>((d) => toModel(d)),
            catchError((err) => {
                this._statusService.sendStatus({variant: 'error', error: err})
                return of(undefined)
            })
        )
    }

    getFilteredList(q: Query<IndividualQuery>): Observable<ItemList<Individual>> {
        return this._httpClient.get<ItemList<Individual>>({url: prepareURL(this._url, q)}).pipe(
            map<ItemList<IndividualDTO>, ItemList<Individual>>((dto) => {
                const itemList = emptyList<Individual>()
                itemList.count = dto.count
                itemList.items = dto.items.map((d) => toModel(d))
                return itemList
            }),
            catchError((err) => {
                this._statusService.sendStatus({variant: 'error', error: err})
                return of(emptyList<Individual>())
            })
        )
    }

    update(e: IndividualDTO): Observable<Individual | undefined> {
        return this._httpClient.put<Individual>({url: this._url, body: e}).pipe(
            map<IndividualDTO, Individual>((d) => toModel(d)),
            catchError((err) => {
                this._statusService.sendStatus({variant: 'error', error: err})
                return of(undefined)
            })
        )
    }

    update2(e: IndividualDTO): Observable<Individual | undefined> {
        return this._httpClient.put<Individual>({url: this._url+"/update", body: e}).pipe(
            map<IndividualDTO, Individual>((d) => toModel(d)),
            catchError((err) => {
                this._statusService.sendStatus({variant: 'error', error: err})
                return of(undefined)
            })
        )
    }

    updateBamFiles(e: IndividualDTO): Observable<Individual | undefined> {
        return this._httpClient.put<Individual>({url: this._url + "/updatebamfiles", body: e}).pipe(
            map<IndividualDTO, Individual>((d) => toModel(d)),
            catchError((err) => {
                this._statusService.sendStatus({variant: 'error', error: err})
                return of(undefined)
            })
        )
    }

    getInformativityList(q: Query<InformativityQuery>): Observable<ItemList<Informativity>> {
        return this._httpClient.get<ItemList<Informativity>>({url: prepareURL(this._url+"/informativity/informativity", q)}).pipe(
            map((dto) => {
                const itemList = emptyList<Informativity>()
                itemList.count = dto.count
                itemList.items = dto.items
                return itemList
            }),
            catchError((err) => {
                this._statusService.sendStatus({variant: 'error', error: err})
                return of(emptyList<Informativity>())
            })
        )
    }

    updateInformativity(e: Informativity): Observable<Informativity | undefined> {
        return this._httpClient.put<Informativity>({url: this._url+"/informativity/update", body: e}).pipe(
            catchError((err) => {
                this._statusService.sendStatus({variant: 'error', error: err})
                return of(undefined)
            })
        )
    }

    runParentsSnipetsProcess(projectID: string): Observable<boolean> {
        return this._httpClient.post<boolean>({url: `${this._url}/runParentsSnipetsProcess/${projectID}`}).pipe(
            catchError((err) => {
                this._statusService.sendStatus({variant: 'error', error: err})
                return of(false)
            })
        )
    }

    getProgress(projectID: string): Observable<ProgressState>{
        return this._httpClient.get<ProgressState>({url: `${this._url}/progress/${projectID}`}).pipe(
            catchError((err) => {
                return of({ percent: 0, message: 'Error', done: false, error: err.message, updatedAt: new Date().toISOString() })
            })
        )
    }

}
